import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Check, Camera, MessageSquare, ShieldCheck, Star, Download } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SITE, IMAGES, RENTAL_PDF } from "@/content/site";

const FREQUENCIES = [
  {
    visits: "1x",
    label: "Weekly Visit",
    text: "Essential professional care for lower-occupancy periods and off-season months — water stays balanced, clean, and always ready for the next booking.",
  },
  {
    visits: "2x",
    label: "Weekly Visits",
    text: "A midweek reset plus a pre-weekend check — built for steady booking calendars where arriving guests should always find clear, inviting water.",
  },
  {
    visits: "3x",
    label: "Weekly Visits",
    text: "Maximum coverage for high-occupancy beach houses and peak season — near-constant water quality control through heavy guest turnover.",
  },
];

const PROMISES = [
  "Guest-ready water checked around your turnover schedule",
  "Photo documentation and prompt issue alerts sent to you or your property manager",
  "Chemistry managed between back-to-back bookings, not just once a week",
  "Priority communication after storms and heavy-use weekends",
  "One local point of contact who knows each property personally",
];

export const ShortTermRentalSection = () => (
  <section data-testid="str-section" className="py-16 sm:py-24 lg:py-32 bg-shell relative overflow-hidden">
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <Reveal className="lg:col-span-7">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Short-Term Rental Pool Service</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
            Your five-star reviews start at the pool
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <p className="text-base text-slate-600 leading-relaxed">
            We&apos;re a trusted pool care partner of multiple property management companies across
            Pinellas County — because in a vacation rental, nothing sinks a review faster than a
            cloudy or green pool on arrival day.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6">
        {FREQUENCIES.map((f, i) => (
          <motion.div
            key={f.visits}
            data-testid={`str-frequency-${i}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className="bg-white rounded-2xl border border-slate-200/80 p-7 sm:p-8 transition-shadow duration-300 hover:shadow-xl hover:shadow-cyan-500/10"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-pool to-aqua">
                {f.visits}
              </span>
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">{f.label}</span>
            </div>
            <p className="mt-4 text-sm sm:text-[15px] text-slate-500 leading-relaxed">{f.text}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-14 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-navy">
              We treat your rental business like our own
            </h3>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              A short-term rental pool isn&apos;t a backyard pool — it&apos;s a revenue asset with a
              public review score attached. Guests arrive expecting hotel-clear water, and they
              photograph everything. Our rental service visits are built around that reality:
              tighter schedules, sharper communication, and zero tolerance for &ldquo;it&apos;ll
              clear up by tomorrow.&rdquo;
            </p>
          </Reveal>
          <ul className="mt-7 space-y-3.5">
            {PROMISES.map((p, i) => (
              <Reveal key={p} delay={0.06 * i}>
                <li data-testid={`str-promise-${i}`} className="flex items-start gap-3.5">
                  <span className="w-7 h-7 rounded-full bg-navy flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-sky2" />
                  </span>
                  <span className="text-sm sm:text-[15px] text-slate-600 leading-relaxed">{p}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-col sm:flex-row gap-3.5">
              <Link
                to="/contact/"
                data-testid="str-partner-button"
                className="group inline-flex items-center justify-center gap-2.5 bg-navy text-white font-bold px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-pool hover:text-white hover:shadow-lg hover:shadow-cyan-500/25"
              >
                Partner With Us
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/vacation-rental-pool-service/"
                data-testid="str-learn-more-link"
                className="group inline-flex items-center justify-center gap-2 font-display font-bold text-pool hover:text-ocean transition-colors px-2 py-3.5"
              >
                Explore rental service
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={RENTAL_PDF}
                download
                data-testid="str-section-pdf-link"
                className="group inline-flex items-center justify-center gap-2 font-display font-bold text-pool hover:text-ocean transition-colors px-2 py-3.5"
              >
                <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                Rental service sheet (PDF)
              </a>
              <a
                href={SITE.phoneHref}
                data-testid="str-call-button"
                className="inline-flex items-center justify-center gap-2.5 border-2 border-navy text-navy font-bold px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-navy hover:text-white"
              >
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 relative">
          <Reveal delay={0.1}>
            <div className="clip-arch overflow-hidden ring-1 ring-slate-200 shadow-2xl shadow-navy/15">
              <img
                src={IMAGES.muralPool}
                alt="Sparkling clear vacation rental pool at a colorful Pinellas County property serviced by Point of Pines Pools"
                className="w-full h-80 sm:h-[26rem] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="absolute -bottom-6 left-4 sm:left-8 right-4 sm:right-auto sm:max-w-sm bg-navy text-white rounded-2xl shadow-2xl shadow-navy/40 p-5 sm:p-6"
            data-testid="str-quote-card"
          >
            <div className="flex gap-1 text-aqua">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-current" />
              ))}
            </div>
            <p className="mt-3 font-display font-semibold tracking-tight leading-snug text-[15px]">
              &ldquo;Your guests rate the pool before they unpack. We make sure it earns five stars
              every single check-in.&rdquo;
            </p>
            <div className="mt-4 flex items-center gap-4 text-[11px] font-mono uppercase tracking-[0.16em] text-sky2/80">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> CPO Certified</span>
              <span className="inline-flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Photo Reports</span>
              <span className="inline-flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Direct Line</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);
